import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { WIZ_EVENT_TYPE, VALUE_ENTER, VALUE_ESCAPE } from '@wizeoo/akm-core-lib';
import {
  FilterComponent,
  WizBasePopUp,
  WizCheckbox,
  WizDatasource,
  WizDropdown,
  WizList,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { VisionFilterKey, VisionKey, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { WizConstants } from '@wizeoo/akm-compute-lib';

import { GridsterWidget } from '../../../../vo/workingzone/gridster-widget';
import { VisionKeyObj } from '../../../../vo/vision/tools/visionkey.obj';
import { FreeDataVO } from '../../../../vo/core/freedata.vo';
import { Model } from '../../../../model/model';
import { FreedataLevelVO } from '../../../../vo/core/freedatalevel.vo';
import { VisionKeyRenderer } from '../../base/vision-key.renderer';
import { VisionStepVO } from '../../../../vo/vision/visionstep.vo';
import { WidgetDataPopDatas } from '../../../../manager/widgets/i-widget';


/**
 * Pop de sélection du périmètre de données du widget (Freedata + level)
 */
@Component({
  selector: 'freedata-widget-data-pop',
  templateUrl: './freedata-widget-data.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreedataWidgetDataPop extends WizBasePopUp implements OnDestroy {
  
  // dimensions demandées
  public static width: string = '520px';
  public static height: string = '400px';
  

  // validation sur enter, sortie sur Esc
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === VALUE_ENTER && this.validable) {
      this.save();
    }
    if (event.key === VALUE_ESCAPE) {
      this.hide();
    }
  }
  
  // listes des clés disponibles
  @ViewChild('lstFreeKeys', { read: WizList }) lstFreeKeys: WizList;  
  /** filtre des clés libres  */
  @ViewChild('lstFreeKeysFilter', { read: FilterComponent, static: false }) lstFreeKeysFilter: FilterComponent;

  @ViewChild('ddMaster', { read: WizDropdown }) ddMaster: WizDropdown; // drop du master
  @ViewChild('cbMefe', { read: WizCheckbox }) cbMefe: WizCheckbox; // cb emptyForEmpty

  /** La datasource de choix de visionStep (de la vision) */
  stepDS: WizDatasource<VisionStepVO> = new WizDatasource<VisionStepVO>()
    .setSortFields([{ property: 'level', way: 'asc', numeric: true }])
    .build();

  /** pool d'axes libres */
  private freeKeyDS: WizDatasource<VisionKeyObj> = new WizDatasource<VisionKeyObj>()
    .setSortFields([{ property: 'code', way: 'asc', numeric: true }])
    .setFilter(this.freeKeysFilterFunc.bind(this))
    .build();

  /** tous les widgets éligibles comme 'Master' */
  public allAgis: WizDatasource<GridsterWidget> = new WizDatasource<GridsterWidget>([])
    .setItemCodeField('uid')
    .setSortFields([{ property: 'indice', way: 'asc', numeric: true }])
    .build();

  /** Renderer des keys */
  public visionKeyRenderer: any = VisionKeyRenderer;

  public currentMaster: GridsterWidget = null; // le maître en cours de sélection

  public datas: WidgetDataPopDatas;

  public get model(): Model {
    return this.datas.model;
  }

  /** accesseur sur le data avec le bon type */
  private get wco(): WidgetConfigurationObj {
    return this.datas.config;
  }

  /** accesseur sur la conf gridster */
  private get gritVO(): GridsterWidget {
    return this.datas.gridsterConfig;
  }

  public get validable(): boolean {
    return !!this.freeKeyDS.selectedItem;
  }

  /** présence d'un master pour checkbox de choix de mode */
  public get hasMaster(): boolean {
    return !!this.allAgis.selectedItem;
  }

  public get emptyToEmpty(): boolean {
    return this.wco.mefe;
  }


  public show(): void {
    this.lstFreeKeysFilter.configure(['code', 'label'], null, null);
    this.feedKeys(); // construction des listes des clés
    this.feedSteps();
    this.feedMasterDS(); // alimentation de la drop des maîtres (chaque changement de FD)

    this.setListeners();
  }

  /** pose des écouteurs  */
  private setListeners(): void {
    WizEventManager.addListener(this, this.lstFreeKeysFilter, WIZ_EVENT_TYPE.FILTER, this.doFilter); // écouteur sur le filtre
    WizEventManager.addListener(this, this.lstFreeKeys, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.setFDKey); // choix du level de FD
    WizEventManager.addListener(this, this.ddMaster, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.setMaster); // choix d'un master

  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, this.lstFreeKeysFilter, WIZ_EVENT_TYPE.FILTER); // écouteur du filtre
    WizEventManager.removeListener(this, this.ddMaster, WIZ_EVENT_TYPE.SELECTION_CHANGED); // choix d'un master
    WizEventManager.removeListener(this, this.lstFreeKeys, WIZ_EVENT_TYPE.SELECTION_CHANGED); // choix du level de FD
 
    // this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * modification du filtre
   * @param e WizEvent
   */
  protected doFilter(e: WizEvent): void {
    this.freeKeyDS.refresh();
  }

  /**
   *
   * filtre des keys disponibles
   * @param item visionKeyObj
   * @returns boolean, true si l'element correspond au filtre, false sinon
   */
  private freeKeysFilterFunc(item: VisionKeyObj): boolean {
    return this.lstFreeKeysFilter.checkVisible(item);
  }

 /**
   * génération de la collection de step pour alimentation de la drop
   * et alimentation du grid si le contexte est alimenté
   */
  private feedSteps(): void {
    let stepArr: VisionStepVO[] = [];
    // le data est un widgetC
    const visionCode = this.wco.v;
    const vision = this.model.visionEngine.getVision(visionCode);
    if (!!vision && !!vision.visionSteps) {
      stepArr = Object.values(vision.visionSteps).map((visionStep) => new VisionStepVO(visionStep));
    }

    this.stepDS.itemCodeField = 'visionAndStep';
    this.stepDS.setSource(stepArr);

   if (this.wco.vs) {
      // visionStep défini
      const selectedVS: VisionStepVO = stepArr.find((vs) => vs.level === this.wco.vs);
      if (!!selectedVS) {
        this.stepDS.select([selectedVS]);
      }
    }
  }



  /** alimentation de la liste des clés */
  public feedKeys(): void {
    // construction du pool de visionKeyObj kind(FDV)#code#level = obj
    const allKeyMap: VisionKeyObj[] = [];
    let key: string;
    let obj: VisionKeyObj;

    const visionCode = this.wco.v;
    const vision = this.model.visionEngine.getVision(visionCode);

    // map des clés & filtres de vision
    const visionKeyMap: Map<string, VisionKey> = new Map(
      Object.values(vision.visionKeys || {}).map((item: VisionKey) => [item.code, item])
    );
    const visionFilterKeyMap: Map<string, VisionFilterKey> = new Map(
      Object.values(vision.visionFilterKeys || {}).map((item: VisionFilterKey) => [item.code, item])
    );


    // alimentation des freeDatas
    Array.from(this.model.freedataEngine.getFreedataMap().values()).forEach((item: FreeDataVO) => {
      if (!item.kindMeasure) {
        if (!item.levelsVOMap || item.levelsVOMap.size === 0) {
          this.model.freedataEngine.addFreeDataLevel(item, this.model.freedataEngine.createFreedatalevel(item,1));
        }

        item.levelsVOMap.forEach((fdvl: FreedataLevelVO) => {
          key = VisionKeyObj.FREEDATA + VisionKeyObj.SEPARATOR + item.code + VisionKeyObj.SEPARATOR + fdvl.level;
          obj = new VisionKeyObj(item, 'free', 0, fdvl.level);
          let smallKey: string = [item.code, fdvl.level].join(WizConstants.CONTEXT_SEPARATOR);
          if(visionKeyMap.has(smallKey) || visionFilterKeyMap.has(smallKey)) {
            allKeyMap.push(obj);
          }
        });
      }
    });

    this.freeKeyDS.setSource(allKeyMap);
    // alimenter la sélection
    const selFD: VisionKeyObj = this.freeKeyDS.getItemByKey(this.wco.fdk);
    this.freeKeyDS.select([selFD]);

    this.lstFreeKeys.wizDataProvider = this.freeKeyDS;
  }

  /** sélection d'un FD.level */
  private setFDKey(e:WizEvent): void {
    this.feedMasterDS(); // alimentation de la drop des maîtres (chaque changement de FD)
  }

  /** alimentation de la DS des maîtres possibles */
  private feedMasterDS(): void {
    this.allAgis.setSource(
        this.model.widgetEngine.availableFreedataMasters( this.model.workingTemplateClone.widgets, this.gritVO,
        this.freeKeyDS.selectedItem)
    ); // alimentation de la DS des Masters possibles TODO6

    if (this.wco.muid) {
      // maître alimenté
      this.currentMaster = this.model.widgetEngine.getWidgetByUid(this.wco.muid);
    }

    if (!!this.currentMaster) {
      // on prend l'instance de la collection
      this.currentMaster = this.allAgis.getItemByKey(this.currentMaster.uid);
    }

    this.allAgis.select([this.currentMaster]);
  }




  /** changement de sélection depuis la drop de master */
  private setMaster(e: WizEvent): void {
    this.currentMaster = this.ddMaster.selectedItem;
  }


  /** sur save, alimentation de l'objet de configuration */
  public save(): void {
    let fdChanged: boolean = false;
    const previousFD: string = this.wco.fdk;
    const previousLevel: number = this.wco.vs;

    this.wco.vs = this.stepDS.selectedItem ? this.stepDS.selectedItem.level : null;

    this.wco.fdk = this.freeKeyDS.selectedItem ? this.freeKeyDS.selectedItem.code : null;
    this.wco.muid = this.allAgis.selectedItem ? this.allAgis.selectedItem.uid : null; // on prend l'uid du master
    this.wco.mefe = this.cbMefe.wizChecked;

    if (previousLevel !== this.wco.vs) {
      fdChanged = true;
    }

    if (previousFD !== this.wco.fdk) {
      this.wco.r = [];
      this.wco.f = [];
      fdChanged = true;
    }

    this.callback(this.wco, fdChanged);
    this.hide();
  }





}
